import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { Location, NgClass, NgTemplateOutlet } from '@angular/common';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LoginResponse, UsersService } from '@app/services/users.service';
import { MixpanelService } from '@app/services/mixpanel.service';
import { takeUntil } from 'rxjs/operators';
import { DeviceService } from '@app/services/device.service';
import { ChangeEffectAnimation } from '@app/animations/change-effect.animation';
import {
    AuthenticateByPhoneService,
    PhoneInterface,
} from '@app/modules/dialogs/rich-verify-phone-dialog/authenticate-by-phone.service';
import { SnackbarService } from '@app/services/snackbar.service';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { ContactsService } from '@app/services/contacts.service';
import phoneCountryCodes from './phone-country-codes.json';
import { AuthenticateEmailService } from '@app/modules/dialogs/rich-verify-phone-dialog/authenticate-by-email.service';
import { DialogService } from '@app/modules/dialogs/dialog.service';
import { environment } from '@env/environment';
import { emailValidator } from '@app/validators/validators';
import { RecaptchaService } from '@app/services/recaptcha-service';
import { Subject } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FlexModule } from '@angular/flex-layout';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxMaskDirective } from 'ngx-mask';
import { AutofocusDirective } from '@app/directives/autofocus.directive';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { HypoInputComponent } from '@app/components/form-components/hypo-input/hypo-input.component';
import version from './../../../../version.json';
import { MortgagesService } from '@app/services/mortgages.service';

export interface CountryCode {
    abbr: string;
    code: string;
    label: string;
    pos: number;
}

export enum LoginSteps {
    EnterEmailOrPhone,
    CheckEmail,
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    animations: [ChangeEffectAnimation.ChangeEffect, ChangeEffectAnimation.ChangeEffectFade],
    imports: [
        MatButtonModule,
        MatProgressSpinnerModule,
        NgClass,
        FlexModule,
        ReactiveFormsModule,
        NgTemplateOutlet,
        MatFormFieldModule,
        MatInputModule,
        NgxMaskDirective,
        AutofocusDirective,
        MatSelectModule,
        NgxMatSelectSearchModule,
        MatRippleModule,
        MatIconModule,
        MatSnackBarModule,
        HypoInputComponent,
    ],
})
export class LoginComponent implements OnInit, OnDestroy {
    private readonly location = inject(Location);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    public readonly usersService = inject(UsersService);
    private readonly mortgagesService = inject(MortgagesService);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly mixpanelService = inject(MixpanelService);
    private readonly deviceService = inject(DeviceService);
    private readonly authenticateByPhoneService = inject(AuthenticateByPhoneService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly contactsService = inject(ContactsService);
    private readonly authenticateByEmailService = inject(AuthenticateEmailService);
    private readonly dialogService = inject(DialogService);
    private readonly recaptchaService = inject(RecaptchaService);

    protected destroyed$: Subject<void> = new Subject<void>();

    LoginSteps: typeof LoginSteps = LoginSteps;
    public changeState: string = 'initial';
    public currentYear = new Date().getFullYear();
    public emailHighlight: boolean = false;
    public isDesktop: boolean;
    public isLogging = false;
    public loginForm: UntypedFormGroup;
    public loginStep = LoginSteps.EnterEmailOrPhone;
    public phoneCountryCodes = phoneCountryCodes;
    public phoneHighlight: boolean = false;
    public showPage = false;
    public snackBarRef: MatSnackBarRef<any>;
    @ViewChild('unknownEmailTemplate', { read: TemplateRef })
    public unknownEmailTemplate!: TemplateRef<any>;
    @ViewChild('unknownPhoneTemplate', { read: TemplateRef })
    public unknownPhoneTemplate!: TemplateRef<any>;
    public phoneInputActive: boolean = true;

    CZ_COUNTRY_CODE: CountryCode;

    constructor() {
        this.CZ_COUNTRY_CODE = (phoneCountryCodes as CountryCode[]).find((code) => code.abbr === 'CZ');
        this._createLoginForm();
        this.deviceService.isDesktopObservable.pipe(takeUntil(this.destroyed$)).subscribe((isDesktop: boolean) => {
            this.isDesktop = isDesktop;
        });
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    private get phoneNumberControl(): FormControl<string> {
        return this.loginForm.get('phoneNumber') as FormControl<string>;
    }

    private get emailControl(): FormControl<string> {
        return this.loginForm.get('email') as FormControl<string>;
    }

    async ngOnInit() {
        const routerSnapshot = this.route.snapshot;
        const urlParams = routerSnapshot.queryParams;
        if (urlParams?.message) {
            const url = this.location.path().split('?')[0];
            this.location.replaceState(url);
            this.displayMessage(urlParams?.message);
            this.showPage = true;
        } else if (urlParams?.userId && urlParams?.token && urlParams?.code) {
            const response = await this._loginPasswordLess(
                urlParams.userId,
                urlParams.token,
                urlParams.code,
                urlParams.redirectUri,
                urlParams.offerId
            );
            if (response.authenticated) {
                if (urlParams.unsubscribe !== undefined) {
                    this.dialogService.showCommunicationPreferencesDialog();
                }
            } else {
                if (response.throttle) {
                    await this.dialogService.showLockoutDialog(response.throttle);
                }
                this.showPage = true;
            }
        } else {
            this.showPage = true;
        }

        // Reverification
        if (
            routerSnapshot.url.map((urlSegment: UrlSegment) => urlSegment.path).includes('reverification') &&
            routerSnapshot.params.code
        ) {
            this.dialogService
                .confirm({
                    title: 'Pokračovat v řešení hypotéky?',
                    message:
                        'Pokračováním na další krok ověříme Váš zájem o řešení hypotéky. Pokud zvolíte Zrušit, proces ukončíme.',
                    okText: 'Pokračovat',
                })
                .subscribe((result) => {
                    if (result) {
                        window.location.href = `${environment.url.smsBackendApi}/${routerSnapshot.params.code}`;
                    } else {
                        this.router.navigate(['/login']);
                    }
                });
        }
    }

    private displayMessage(message: string) {
        if (message === 'Token is no longer valid!' || message === 'token_expired') {
            this.snackbarService.showError('Vypršela platnost přihlášení. Přihlašte se prosím znovu.');
        } else {
            this.snackbarService.showError('Špatné přihlašovací údaje.');
        }
    }

    public onChangeEffect($event) {
        this.changeState = ChangeEffectAnimation.onChangeEffect(this.changeState, $event);
    }

    public async submitForm() {
        this.isLogging = true;

        const phoneNumber = this.loginForm.get('phoneNumber').value;
        const phoneCountryCode = this.loginForm.get('phoneCountryCode').value.code;
        const email = this.loginForm.get('email').value;

        let repeatRequest = false;

        do {
            repeatRequest = false;
            try {
                if (phoneNumber) {
                    const res = await this.loginByPhone();
                    if (res.errorCode === 429) {
                        this.mixpanelService.track('$verification_dialog_login_rate_limit', {
                            phoneNumber,
                            phoneCountryCode,
                            source: `${version.buildVersion}-banking`,
                        });
                        repeatRequest = await this.dialogService.showThrottleDialog(phoneNumber, phoneCountryCode);
                    }
                } else if (email) {
                    await this.loginByEmail();
                }
            } catch (err) {
                console.error('LoginComponent', err);
            } finally {
                this.isLogging = false;
            }
        } while (repeatRequest);
    }

    private async _loginPasswordLess(
        userId: string,
        token: string,
        code: string,
        redirectUri?: string,
        mortgageId?: string
    ): Promise<LoginResponse> {
        this.isLogging = true;
        let response: LoginResponse = { authenticated: false };

        try {
            response = await this.usersService.loginPasswordLessByUserId(userId, token, code);
            if (response.authenticated) {
                if (redirectUri) {
                    const urlFragments = redirectUri.split('/');
                    if (this.isDesktop) {
                        await this.router.navigate([...urlFragments]);
                    } else {
                        await this.router.navigate(['/mobile', ...urlFragments]);
                    }
                } else if (mortgageId) {
                    await this.navigateToLastStepInOffer(mortgageId);
                } else {
                    await this.router.navigate(['/dashboard']);
                }
            } else {
                this.showPage = true;
            }
        } finally {
            this.isLogging = false;
        }

        return response;
    }

    public changeStep(loginStep: LoginSteps = LoginSteps.EnterEmailOrPhone) {
        if (loginStep < this.loginStep) {
            this.changeState = 'initialReverse';
        } else {
            this.changeState = 'final';
        }
        this.loginStep = loginStep;
    }

    private async navigateToLastStepInOffer(mortgageId: string) {
        // @API cases/mortgage.get
        const mortgage = await this.mortgagesService.get(mortgageId);
        await this.mortgagesService.setMortgage(mortgage);

        if (mortgage.isCanceled) {
            return await this.successfullyAuthenticated();
        } else {
            return await this.mortgagesService.navigateToActualStep(mortgage);
        }
    }

    private async successfullyAuthenticated(): Promise<void> {
        this.mixpanelService.track('$login_button');

        await this.router.navigate(['/dashboard']);
        this.isLogging = false;
    }

    private async loginByPhone(): Promise<LoginResponse> {
        let res: LoginResponse = { authenticated: false };
        try {
            const phone: PhoneInterface = {
                phoneNumber: this.loginForm.get('phoneNumber').value,
                phoneCountryCode: this.loginForm.get('phoneCountryCode').value.code,
            };

            res = await this.authenticateByPhoneService.login(phone);
            const authenticated = res.authenticated;
            if (authenticated) {
                await this.successfullyAuthenticated();
            } else {
                const errorCode = res.errorCode;
                if (res.throttle) {
                    await this.dialogService.showLockoutDialog(res.throttle);
                } else if (errorCode === 403) {
                    this.dialogService.showBlacklistDialog();
                } else if (errorCode === 404) {
                    this.snackBarRef = this.snackbarService.showErrorWithConfirmation(this.unknownPhoneTemplate);
                    this.loginForm.get('phoneNumber')?.reset();
                    this.emailHighlight = true;
                    this.phoneHighlight = false;
                    this.phoneInputActive = false;
                } else if (errorCode === 422) {
                    this.dialogService.showTooManyRequestsDialog();
                } else {
                    this.changeStep(LoginSteps.EnterEmailOrPhone);
                    this.isLogging = false;
                }
            }
        } catch (err) {
            console.error('AuthenticateByPhoneService.sendVerificationSms exception', err);
            throw err;
        }

        return res;
    }

    private async loginByEmail() {
        const email = this.loginForm.get('email').value;
        const gcToken = await this.recaptchaService.check('contacts/check');
        const response = await this.contactsService.checkEmail(email, gcToken);
        const { exist, isVerify: emailVerified = false, contactId = '' } = response;

        if (emailVerified) {
            this.mixpanelService.track('$send_magic_link');
            this.changeStep(LoginSteps.CheckEmail);
        } else if (exist) {
            this.isLogging = true;
            const authenticated = await this.authenticateByEmailService.login(email, contactId);
            if (authenticated) {
                await this.successfullyAuthenticated();
            }
        } else {
            this.changeStep(LoginSteps.EnterEmailOrPhone);
            this.snackBarRef = this.snackbarService.showErrorWithConfirmation(this.unknownEmailTemplate);
            this.phoneHighlight = true;
            this.emailHighlight = false;
            this.phoneInputActive = true;
            this.loginForm.get('email')?.reset();
        }
    }

    private _createLoginForm() {
        this.loginForm = this.fb.group({
            phoneCountryCode: [this.CZ_COUNTRY_CODE],
            phoneNumber: [''],
            phoneCountryCodeSearch: [''],
            email: ['', [emailValidator]],
            password: ['', [Validators.required]],
        });

        this.phoneNumberControl.valueChanges.subscribe(() => {
            const countryCode = this.loginForm.get('phoneCountryCode')?.value;
            const phone = this.loginForm.get('phoneNumber')?.value;

            if (countryCode === '+420' || countryCode === '+421') {
                const pattern = new RegExp('[0-9]{9}');
                const isValid = pattern.test(phone) && phone.length === 9;
                if (isValid) {
                    this.emailControl.reset('', { emitEvent: false });
                }
            } else {
                const pattern = new RegExp('^[0-9]+$', 'gm');
                const isValid = pattern.test(phone) && phone.length > 3;
                if (isValid) {
                    this.emailControl.reset('', { emitEvent: false });
                }
            }
        });

        this.emailControl.valueChanges.subscribe((email) => {
            if (this.emailControl.valid && email?.length > 0) {
                this.phoneNumberControl.reset('', { emitEvent: false });
            }
        });
    }
}
